import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";

// Styles import (deduplicated)
import "./css/all.css";
import "./css/animate.min.css";
import "./css/bootstrap.min.css";
import "./css/carousel.css";
import "./css/colors.css";
import "./css/lightbox.css";
import "./css/style.css";
import "./css/responsive.css";
import "./css/my.css";

// Data import
import navData from "./data/nav.json";
import sponsorData from "./data/sponsor.json";
import newsData from "./data/news.json";
import cdsData from "./data/cds.json";
import testimonialsData from "./data/testimonials.json";
import concertData2026 from "./data/concerts/2026.json";
import concertData2027 from "./data/concerts/2027.json";

// Components import
import Navbar from "./components/nav/Navbar";
import Home from "./components/home/Home";
import Footer from "./components/Footer";
import Contacts from "./components/Contacts";
import NotFound from "./components/NotFound";
import ConditionalWelcome from "./components/welcome/ConditionalWelcome";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Support from "./components/Support";
import Transparency from "./components/transparency/Transparency";
import Concerts from "./components/concerts/Concerts";
import ConcertDetails from "./components/concerts/ConcertDetails";
import Archive from "./components/archive/Archive";
import Curriculum from "./components/Curriculum";
import About from "./components/About";
import Conductor from "./components/Conductor";
import Soloists from "./components/Soloists";
import Dedications from "./components/Dedications";
import Reviews from "./components/Reviews";
import News from "./components/news/News";
import NewsDetails from "./components/news/NewsDetails";
import Discography from "./components/discography/Discography";
import MagazinesBooks from "./components/magazines/MagazinesBooks";
import Testimonials from "./components/testimonials/Testimonials";
import FirstExecutions from "./components/first/FirstExecutions";

// Common UI and SEO components
import ScrollToTop from "./components/common/ScrollToTop";
import ScrollObserver from "./components/common/ScrollObserver";
import BackToTop from "./components/common/BackToTop";

function App() {
  const languages = [
    {
      code: "it",
      name: "Italiano",
      country_code: "it",
    },
    {
      code: "en",
      name: "English",
      country_code: "gb",
    },
  ];

  // Merges the two files in a single array! Useful when you have scheduled concerts in 2 different years
  const concertData = [...concertData2026, ...concertData2027];

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!document.title) {
      document.title = t("app_title");
    }
  }, [t]);

  // string manipulation for \n in json file
  const renderBody = (body) => {
    if (!body) return null;
    return body.split(/\n+/).map((line, index) => <p key={index}>{line}</p>);
  };

  const formatDate = (dateString, lang) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const locale = lang === "en" ? enUS : it;
      return format(date, "dd MMMM yyyy", { locale });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Router>
      <ScrollToTop />
      <ScrollObserver />
      <div className="App">
        <Navbar languages={languages} data={navData} />
        <div className="content">
          <ConditionalWelcome />
          <Routes>
            <Route
              exact
              path="/"
              element={
                <Home
                  concerts={concertData}
                  news={newsData}
                  cds={cdsData}
                  testimonials={testimonialsData}
                  formatDate={(dateString) =>
                    formatDate(dateString, i18n.language)
                  }
                />
              }
            />
            <Route
              path="/concerts"
              element={<Concerts concerts={concertData} />}
            />
            <Route
              path="/concerts/:id"
              element={
                <ConcertDetails
                  concerts={concertData}
                  renderBody={renderBody}
                  formatDate={(dateString) =>
                    formatDate(dateString, i18n.language)
                  }
                />
              }
            />
            <Route
              path="/discography"
              element={<Discography cds={cdsData} renderBody={renderBody} />}
            />
            <Route
              path="/magazines"
              element={<MagazinesBooks renderBody={renderBody} />}
            />
            <Route
              path="/first_executions"
              element={<FirstExecutions renderBody={renderBody} />}
            />
            <Route
              path="/testimonials"
              element={<Testimonials testimonials={testimonialsData} />}
            />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/soloists" element={<Soloists />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route
              path="/news"
              element={
                <News
                  news={newsData}
                  formatDate={(dateString) =>
                    formatDate(dateString, i18n.language)
                  }
                />
              }
            />
            <Route
              path="/news/:id"
              element={
                <NewsDetails
                  formatDate={(dateString) =>
                    formatDate(dateString, i18n.language)
                  }
                  renderBody={renderBody}
                />
              }
            />
            <Route path="/dedications" element={<Dedications />} />
            <Route
              path="/conductor"
              element={<Conductor renderBody={renderBody} />}
            />
            <Route
              path="/curriculum"
              element={<Curriculum renderBody={renderBody} />}
            />
            <Route path="/about" element={<About renderBody={renderBody} />} />
            <Route
              path="/archive"
              element={
                <Archive
                  renderBody={renderBody}
                  formatDate={(dateString) =>
                    formatDate(dateString, i18n.language)
                  }
                />
              }
            />
            <Route
              path="/transparency"
              element={
                <Transparency formatDate={formatDate} renderBody={renderBody} />
              }
            />
            <Route
              path="/support"
              element={
                <Support renderBody={renderBody} sponsors={sponsorData} />
              }
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicy renderBody={renderBody} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <Footer data={navData} />
        <BackToTop />
      </div>
    </Router>
  );
}

export default App;
